import { cva, type VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import { Label } from "./ui/label";
import { Stack } from "./ui/stack";
import { Switch } from "./ui/switch";
import { SwitchProps } from "./ui/switch/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Typography } from "./ui/typography";

const switchWithLabelVariants = cva("", {
  variants: {
    variant: {
      default: "",
      wideTrack:
        "h-6 w-12 [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-6 [&>span]:data-[state=unchecked]:translate-x-0",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface SwitchWithLabelProps
  extends Omit<SwitchProps, "checked" | "onCheckedChange" | "onChange">,
    VariantProps<typeof switchWithLabelVariants> {
  label: string;
  labelClassName?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: ReactNode | null;
  disabledTooltip?: ReactNode | string;
}

const SwitchWithLabelBase = ({
  label,
  labelClassName,
  checked,
  onChange,
  description,
  variant,
  className,
  ...props
}: Omit<SwitchWithLabelProps, "disabledTooltip">) => (
  <Stack gap="none">
    <Stack direction="row" align="center" gap="none">
      <Stack className="p-3 pl-0">
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          className={switchWithLabelVariants({ variant })}
          {...props}
        />
      </Stack>
      <Label className={labelClassName}>{label}</Label>
    </Stack>
    {!!description && (
      <Typography variant="captionW1" textColor="tertiary">
        {description}
      </Typography>
    )}
  </Stack>
);

export const SwitchWithLabel = ({
  disabledTooltip,
  ...props
}: SwitchWithLabelProps) => {
  if (disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <SwitchWithLabelBase {...props} />
          </div>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return <SwitchWithLabelBase {...props} />;
};
